import { Controller } from "@hotwired/stimulus"

// Client-side validation: required, email format, phone (10-digit Indian),
// password complexity (8+ chars, upper+lower+number), min/max length.
// DaisyUI input-error class + error text below field.
// Two modes: "blur" (default) — validate immediately on blur.
//            "submit-first" — validate on blur only AFTER first submit attempt.
// Progressive enhancement only (server validation is authoritative).

export default class extends Controller {
  static targets = ["field"]
  static values = { mode: { type: String, default: "blur" } }

  connect() {
    this._submitted = false

    // Suppress browser native validation tooltips
    this.element.setAttribute("novalidate", "")

    // Intercept form submit for validation
    this.element.addEventListener("submit", this._handleSubmit.bind(this))

    // Clear stale errors as user types (e.g., required error clears when field gets a value)
    this.element.addEventListener("input", this._handleInput.bind(this))

    // Select elements fire "change" not "input" — handle both
    this.element.addEventListener("change", this._handleInput.bind(this))
  }

  // Public action: blur->form-validation#validate
  validate(event) {
    // In submit-first mode, skip blur validation until first submit
    if (this.modeValue === "submit-first" && !this._submitted) return
    this._validate(event.target)
  }

  _handleSubmit(event) {
    this._submitted = true
    let hasErrors = false

    this.fieldTargets.forEach(field => {
      this._validate(field)
      if (field.classList.contains("input-error")) {
        hasErrors = true
      }
    })

    // Also check required fields without explicit data-validation
    this.element.querySelectorAll("[required]").forEach(field => {
      if (!field.value.trim()) {
        if (!field.classList.contains("input-error")) {
          this._showError(field, `${this._getFieldLabel(field)} can't be blank`)
          hasErrors = true
        }
      }
    })

    if (hasErrors) {
      event.preventDefault()
      event.stopPropagation()
      // Focus first error field
      const firstError = this.element.querySelector(".input-error")
      if (firstError) firstError.focus()
    }
  }

  _handleInput(event) {
    const field = event.target
    if (!field.matches("input, textarea, select")) return

    // Only re-validate if field currently has an error — clears it if value now passes
    const formControl = field.closest(".form-control")
    const container = formControl || field.parentNode
    if (container && container.querySelector("[data-validation-error]")) {
      this._validate(field)
    }
  }

  _validate(field) {
    this._clearError(field)

    const value = field.value.trim()
    const validation = field.dataset.validation || ""
    const rules = validation.split(" ").filter(Boolean)

    for (const rule of rules) {
      const error = this._checkRule(rule, field, value)
      if (error) {
        this._showError(field, error)
        return
      }
    }

    // Check required from HTML attribute
    if (field.required && !value) {
      this._showError(field, `${this._getFieldLabel(field)} can't be blank`)
      return
    }
  }

  _checkRule(rule, field, value) {
    // Cross-field rules must run even when this field is empty
    const crossFieldRules = ["contact-required", "password-confirm"]
    if (!value && !field.required && !crossFieldRules.includes(rule)) return null
    const label = this._getFieldLabel(field)

    switch (rule) {
      case "required":
        return !value ? `${label} can't be blank` : null

      case "email":
        return value && !/^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/.test(value) ? `${label} must be a valid email address` : null

      case "phone":
        return value && !/^[6-9]\d{9}$/.test(value) ? `${label} must be a valid 10-digit Indian mobile number (starts with 6-9)` : null

      case "org-phone":
        return value && !/^[6-9]\d{9}$/.test(value) ? `${label} must be a valid 10-digit Indian mobile number (starts with 6-9)` : null

      case "name":
        return value && !/^[a-zA-Z][a-zA-Z\s'.\-]*[a-zA-Z.]$/.test(value) ? `${label} must contain only letters, spaces, hyphens, apostrophes, and periods` : null

      case "password":
        if (!value) return null
        if (value.length < 8) return `${label} must be at least 8 characters`
        if (!/[A-Z]/.test(value)) return `${label} must include an uppercase letter`
        if (!/[a-z]/.test(value)) return `${label} must include a lowercase letter`
        if (!/[0-9]/.test(value)) return `${label} must include a number`
        return null

      case "password-confirm": {
        const targetSelector = field.dataset.confirmTarget
        const targetField = targetSelector ? this.element.querySelector(targetSelector) : null
        if (targetField && value && value !== targetField.value) return "Passwords don't match"
        return null
      }

      case "contact-required": {
        // At least one of contact_email or contact_phone must be filled
        const emailField = this.element.querySelector("[data-validation~='email']")
        const phoneField = this.element.querySelector("[data-validation~='org-phone']")
        const emailVal = emailField ? emailField.value.trim() : ""
        const phoneVal = phoneField ? phoneField.value.trim() : ""
        if (!emailVal && !phoneVal) return "At least one of Contact Email or Contact Number is required"
        return null
      }

      default:
        // min-N, max-N
        if (rule.startsWith("min-")) {
          const min = parseInt(rule.replace("min-", ""), 10)
          return value && value.length < min ? `${label} must be at least ${min} characters` : null
        }
        if (rule.startsWith("max-")) {
          const max = parseInt(rule.replace("max-", ""), 10)
          return value && value.length > max ? `${label} must be at most ${max} characters` : null
        }
        return null
    }
  }

  _showError(field, message) {
    // Add error styling to the input itself
    field.classList.add("input-error")

    // Also add to parent label wrapper if inside one (DaisyUI pattern)
    const labelWrapper = field.closest("label.input")
    if (labelWrapper) {
      labelWrapper.classList.add("input-error")
    }

    const errorEl = document.createElement("p")
    errorEl.className = "text-error text-xs mt-1"
    errorEl.setAttribute("data-validation-error", "")
    errorEl.textContent = message

    // Append to .form-control container (below the input wrapper)
    const formControl = field.closest(".form-control")
    if (formControl) {
      formControl.appendChild(errorEl)
    } else {
      // Fallback: append after the label wrapper or parent
      const wrapper = labelWrapper || field.parentNode
      wrapper.parentNode.insertBefore(errorEl, wrapper.nextSibling)
    }
  }

  _clearError(field) {
    field.classList.remove("input-error")

    // Also clear from parent label wrapper
    const labelWrapper = field.closest("label.input")
    if (labelWrapper) {
      labelWrapper.classList.remove("input-error")
    }

    // Remove error from .form-control or parent
    const formControl = field.closest(".form-control")
    const container = formControl || field.parentNode
    const existing = container.querySelector("[data-validation-error]")
    if (existing) existing.remove()
  }

  _getFieldLabel(field) {
    const label = field.closest(".form-control")?.querySelector("label .label-text")
    return label?.textContent?.replace(/\s*\*\s*$/, "").trim() || "Field"
  }
}
